/** On a medicine or vaccine product page: its label values, and which animals got each lot. */
import { useState } from "react";
import { Link } from "react-router";
import { ApiError, api, qs } from "../../../web/lib/api";
import { formatDate, useFormat } from "../../../web/lib/format";
import { t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { SchemaForm, issuesToErrors, type Errors, type Values } from "../../../web/forms/SchemaForm";
import { registry } from "../../../web/registry";
import { DataList } from "../../../web/shell/describe";
import { Button, Card, Chip, Empty, Loading, Sheet, cx, toast } from "../../../web/ui/kit";
import type { TraceAnswer } from "../shared/dto";
import { API } from "./common";

type Stock = Record<string, any> & { id: string; kind: string; attrs: Record<string, unknown>; lots: Array<{ id: string; code: string | null; expires_at: string | null }> };

export const isHealthProduct = (s: Record<string, any>) => s.kind === "medicine" || s.kind === "vaccine";

export function ProductLabel({ entity }: { entity: Record<string, any> }) {
  useLanguage();
  const s = entity as Stock;
  const can = useCan();
  const fmt = useFormat();
  const [editing, setEditing] = useState(false);
  const fields = registry.stockKinds.get(s.kind)?.fields ?? {};
  const empty = !Object.keys(s.attrs ?? {}).some((k) => k in fields);
  return (
    <Card>
      {empty ? <p className="muted">{t("No label values yet. Add the dose and withdrawal so treatments fill them in.")}</p> : <DataList fields={fields} data={s.attrs} fmt={fmt} />}
      {can("manage") && <div className="row-actions health-label-actions"><Button size="sm" icon="edit" onClick={() => setEditing(true)}>{empty ? t("Add label values") : t("Edit label values")}</Button></div>}
      {editing && <LabelSheet stock={s} onClose={() => setEditing(false)} />}
    </Card>
  );
}

function LabelSheet({ stock, onClose }: { stock: Stock; onClose: () => void }) {
  const fields = registry.stockKinds.get(stock.kind)?.fields ?? {};
  const [value, setValue] = useState<Values>(Object.fromEntries(Object.entries(stock.attrs ?? {}).filter(([k]) => k in fields)));
  const [errors, setErrors] = useState<Errors>({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`${API}/products/${stock.id}/label`, value);
      await invalidateAll();
      toast(t("Saved. New treatments use these values; past ones keep what was recorded."), { tone: "good" });
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) setErrors(issuesToErrors(err.issues, ""));
      else toast(err instanceof Error ? err.message : t("Something went wrong on our side. Try again."), { tone: "bad" });
    } finally { setBusy(false); }
  };
  return (
    <Sheet open onClose={onClose} title={t("Label values")}>
      <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <p className="section-hint">{t("Copy these from the product label or your vet's instructions.")}</p>
        <SchemaForm fields={fields} value={value} onChange={setValue} errors={errors} />
        <div className="form-actions">
          <Button variant="primary" size="lg" type="submit" icon="check" busy={busy}>{t("Save")}</Button>
          <Button variant="ghost" onClick={onClose}>{t("Cancel")}</Button>
        </div>
      </form>
    </Sheet>
  );
}

/** Recall trace: every animal recorded with a lot, without changing anyone's records. */
export function LotTrace({ entity }: { entity: Record<string, any> }) {
  useLanguage();
  const s = entity as Stock;
  const [lot, setLot] = useState<string | null>(s.lots.length === 1 ? s.lots[0].id : null);
  const q = useApi<TraceAnswer>(`${API}/trace${qs(lot ? { lot } : { product: s.id })}`);
  const animals = q.data?.animals ?? [];
  return (
    <div className="stack">
      {s.lots.length > 1 && (
        <div className="choice-chips">
          <button type="button" className={cx("choice", !lot && "choice-on")} onClick={() => setLot(null)}>{t("All lots")}</button>
          {s.lots.map((l) => <button key={l.id} type="button" className={cx("choice", lot === l.id && "choice-on")} onClick={() => setLot(l.id)}>{l.code ?? t("No lot number")}{l.expires_at ? ` · ${formatDate(l.expires_at)}` : ""}</button>)}
        </div>
      )}
      {q.isLoading ? <Loading /> : animals.length === 0 ? <Empty icon="syringe" title={t("Not given to any animal yet")} /> : (
        <>
          <p className="section-hint">{animals.length === 1
            ? t("{{count}} animal got this, in {{records}} records.", { count: 1, records: q.data!.events.length })
            : t("{{count}} animals got this, in {{records}} records.", { count: animals.length, records: q.data!.events.length })}</p>
          <div className="health-trace">
            {animals.map((a) => (
              <Link key={a.id} to={`/cattle/${a.id}?tab=records`} className={cx("chip", a.status === "active" ? "tone-neutral" : "tone-info")}>
                <span>{a.label}</span>{a.status !== "active" && <Chip tone="info">{t(a.status === "sold" ? "Sold" : a.status === "dead" ? "Dead" : a.status)}</Chip>}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
